import type {MessageApiInjection, MessageReactive} from 'naive-ui/es/message/src/MessageProvider'
import {defineStore} from 'pinia'
import {h, type VNodeChild} from "vue";
import {NAlert, NTime} from "naive-ui";
import {allValidationErrorCodes, replaceCharacters} from './i-error-code';
import type {useI18n} from "vue-i18n";
import type {NotificationApiInjection} from 'naive-ui/es/notification/src/NotificationProvider';
import {useAuthStore} from "@/stores/authentication-store";
import type {ErrorDetail} from "@/models/error/error-detail";

interface IErrorCodeHandlerState {
    errorMessage: MessageApiInjection;
    notification: NotificationApiInjection;
    message: MessageReactive | undefined;
    duration: number;
    i18n: ReturnType<typeof useI18n>;
}

export const useMessageHandler = defineStore({
    id: 'error',
    state: () => ({
        duration: 5000,
        message: undefined,
    } as IErrorCodeHandlerState),

    actions: {
        async handleError(error: any) {
            //Error code exception is the default handler, which will contain error data
            if (error.status === 401) {
                const authStore = useAuthStore();
                await authStore.handleSessionExpired();
                this.showErrorMessage("SESSION_EXPIRED");
                return;
            }
            if (error.status === 403) {
                const authStore = useAuthStore();
                await authStore.logout();
                this.showErrorMessage("NO_ACCESS")
                return;
            }
            if (error.data.errorData) {
                this.handleValidationError(error.data.errorData);
            } else {
                this.showErrorMessage(allValidationErrorCodes.DEFAULT);
            }
        },
        handleValidationError(errors: any, destroyAll: boolean = true) {
            if (destroyAll) {
                this.errorMessage.destroyAll();
            }

            for (const property in errors) {
                if (errors[property].length) {
                    const errorDetails: any[] = this.mapErrors(errors[property] as ErrorDetail[], property);

                    for (const errorDetail of errorDetails) {
                        const errorBody = errorDetail.customState
                            ? this.i18n.t(`error_codes.${errorDetail.errorMessage}`, [errorDetail.customState])
                            : this.i18n.t(`error_codes.${errorDetail.errorMessage}`);

                        this.errorMessage.error("", {
                            render: () => {
                                return h(
                                    NAlert,
                                    {
                                        closable: true,
                                        type: 'error',
                                        title: this.i18n.t('error_codes.DEFAULT'),
                                        style: {
                                            maxWidth: "30vw"
                                        }
                                    },
                                    {
                                        default: () => h('div', {'innerHTML': errorBody}),
                                    }
                                )
                            },
                            // 10 seconds
                            duration: 10000
                        });
                    }
                }
            }
        },
        mapErrors(errors: ErrorDetail[], property: string | undefined = undefined) {
            return errors.map(e => {
                if (e.errorMessage in allValidationErrorCodes) {
                    // @ts-ignore
                    return allValidationErrorCodes[e.errorCode]
                        .replace(replaceCharacters, property ?? "");
                }
                return e;
            });
        },
        showErrorMessage(message: string, destroyAll: boolean = true) {
            if (destroyAll) {
                this.errorMessage.destroyAll();
            }

            this.errorMessage.error(this.i18n.t(`error_codes.${message}`), {duration: this.duration});
        },
        /**
         * @param options.values meldingen met meerdere waarden ({transportCount}, {unplannedCount}, ...);
         *        descriptionDataReplacement blijft voor de bestaande aanroepen met één positionele waarde.
         * @param options.duration `null` laat de melding staan tot de gebruiker hem wegklikt. Bedoeld voor
         *        uitkomsten die de gebruiker moet zien ook als hij even niet keek — een achtergrondtaak die
         *        minuten duurt en dan meldt dat er iets níét gelukt is, mag niet na een paar tellen vervagen.
         */
        showNotification(title: string, description: string, type: string = 'success', content: (() => VNodeChild) = () => {
        }, descriptionDataReplacement: [string, string] = ["", ""],
           options?: { values?: Record<string, unknown>; duration?: number | null }) {
            if (options?.values !== undefined) {
                description = this.i18n.t(description, options.values);
            } else {
                description = descriptionDataReplacement[0] === ""
                    ? this.i18n.t(description)
                    : this.i18n.t(description, [descriptionDataReplacement[1]]);
            }

            this.notification.create({
                //@ts-ignore
                type: type,
                title: this.i18n.t(title),
                description: description,
                meta: () => h(NTime, {time: new Date()}),
                content: content,
                // undefined = naive-ui sluit niet vanzelf.
                duration: options?.duration === null ? undefined : options?.duration ?? 7500,
            });
        }
    }
})
